import { supabase, type Database } from '../lib/supabase'
import { apiCall } from '../utils/networkErrorHandler'
import { logServiceError } from '../utils/errorLogger'

type SettingRow = Database['public']['Tables']['settings']['Row']
type SettingInsert = Database['public']['Tables']['settings']['Insert']
type SettingUpdate = Database['public']['Tables']['settings']['Update']

export interface Setting {
  id: string
  key: string
  value: any
  description?: string
  category: string
  isEncrypted: boolean
  createdAt: Date
  updatedAt: Date
}

export interface LineNotificationSettings {
  enabled: boolean
  channelAccessToken: string
  webhookUrl: string
  templates: Record<string, any>
  retryAttempts: number
  retryDelay: number
}



export interface UISettings {
  theme: 'light' | 'dark'
  language: 'th' | 'en'
  showConnectionStatus: boolean
}

export class SettingsService {
  /**
   * Convert value to boolean, handling various input types
   */
  private toBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') return value.toLowerCase() === 'true'
    if (typeof value === 'number') return value === 1
    return Boolean(value)
  }

  /**
   * Convert database row to Setting interface
   */
  private mapRowToSetting(row: SettingRow): Setting {
    if (!row || !row.id) {
      throw new Error('Invalid setting row: missing required fields')
    }
    
    return {
      id: row.id,
      key: row.key,
      value: row.value,
      description: row.description || undefined,
      category: row.category || 'general',
      isEncrypted: row.is_encrypted || false,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at)
    }
  }

  /**
   * Get a setting by key
   */
  async getSetting(key: string): Promise<Setting | null> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('key', key)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Setting not found
        }
        throw new Error(`Failed to get setting: ${error.message}`)
      }

      return this.mapRowToSetting(data)
    }, 'getSetting')
  }

  /**
   * Get setting value by key (convenience method)
   */
  async getSettingValue<T = any>(key: string, defaultValue?: T): Promise<T> {
    try {
      const setting = await this.getSetting(key)
      if (setting && setting.value !== undefined && setting.value !== null) {
        return setting.value
      }
      return defaultValue as T
    } catch (error) {
      console.warn(`Failed to get setting value for key: ${key}, using default:`, defaultValue, error)
      return defaultValue as T
    }
  }

  /**
   * Set a setting value
   */
  async setSetting(key: string, value: any, description?: string, category: string = 'general'): Promise<Setting> {
    console.log(`🔑 setSetting: key=${key}, category=${category}`)
    return apiCall(async () => {
      // First try to update existing setting
      const { data: updateData, error: updateError } = await supabase
        .from('settings')
        .update({
          value,
          description,
          category,
          updated_at: new Date().toISOString()
        })
        .eq('key', key)
        .select()
        .single()

      if (updateError && updateError.code !== 'PGRST116') {
        console.error(`❌ Update failed for ${key}:`, updateError)
        throw new Error(`Failed to update setting: ${updateError.message}`)
      }

      // If update succeeded, return the updated data
      if (updateData) {
        console.log(`✅ Updated ${key} successfully`)
        return this.mapRowToSetting(updateData)
      }

      // If no rows were updated (setting doesn't exist), insert new one
      console.log(`➕ Setting ${key} not found, inserting new...`)
      const { data: insertData, error: insertError } = await supabase
        .from('settings')
        .insert({
          key,
          value,
          description,
          category
        })
        .select()
        .single()

      if (insertError) {
        // If insert fails due to duplicate key, try update one more time
        if (insertError.code === '23505') {
          console.log(`🔄 Duplicate key ${key}, retrying update...`)
          const { data: retryData, error: retryError } = await supabase
            .from('settings')
            .update({
              value,
              description,
              category,
              updated_at: new Date().toISOString()
            })
            .eq('key', key)
            .select()
            .single()

          if (retryError) {
            console.error(`❌ Retry failed for ${key}:`, retryError)
            throw new Error(`Failed to set setting after retry: ${retryError.message}`)
          }

          console.log(`✅ Updated ${key} on retry`)
          return this.mapRowToSetting(retryData)
        }

        console.error(`❌ Insert failed for ${key}:`, insertError)
        throw new Error(`Failed to insert setting: ${insertError.message}`)
      }

      console.log(`✅ Inserted ${key} successfully`)
      return this.mapRowToSetting(insertData)
    }, 'setSetting')
  }

  /**
   * Get all settings by category
   */
  async getSettingsByCategory(category: string): Promise<Setting[]> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('category', category)
        .order('key')

      if (error) {
        throw new Error(`Failed to get settings by category: ${error.message}`)
      }

      return data.map((row: SettingRow) => this.mapRowToSetting(row))
    }, 'getSettingsByCategory')
  }

  /**
   * Get all settings
   */
  async getAllSettings(): Promise<Setting[]> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true })

      if (error) {
        throw new Error(`Failed to get all settings: ${error.message}`)
      }

      return data.map((row: SettingRow) => this.mapRowToSetting(row))
    }, 'getAllSettings')
  }

  /**
   * Delete a setting
   */
  async deleteSetting(key: string): Promise<boolean> {
    return apiCall(async () => {
      const { error } = await supabase
        .from('settings')
        .delete()
        .eq('key', key)

      if (error) {
        throw new Error(`Failed to delete setting: ${error.message}`)
      }

      return true
    }, 'deleteSetting')
  }

  /**
   * Get LINE notification settings
   */
  async getLineNotificationSettings(): Promise<LineNotificationSettings> {
    try {
      const [
        enabled,
        channelAccessToken,
        webhookUrl,
        templates,
        retryAttempts,
        retryDelay
      ] = await Promise.all([
        this.getSettingValue('line_notifications_enabled', false),
        this.getSettingValue('line_channel_access_token', ''),
        this.getSettingValue('line_webhook_url', ''),
        this.getSettingValue('line_notification_templates', {}),
        this.getSettingValue('notification_retry_attempts', 3),
        this.getSettingValue('notification_retry_delay', 1000)
      ])

      return {
        enabled: this.toBooleanValue(enabled),
        channelAccessToken: channelAccessToken || '',
        webhookUrl: webhookUrl || '',
        templates: templates || {},
        retryAttempts: Number(retryAttempts) || 3,
        retryDelay: Number(retryDelay) || 1000
      }
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'SettingsService',
        'getLineNotificationSettings'
      )
      
      // Return default settings on error
      return {
        enabled: false,
        channelAccessToken: '',
        webhookUrl: '',
        templates: {},
        retryAttempts: 3,
        retryDelay: 1000
      }
    }
  }

  /**
   * Update LINE notification settings
   */
  async updateLineNotificationSettings(settings: Partial<LineNotificationSettings>): Promise<void> {
    console.log('🔧 SettingsService: updateLineNotificationSettings called with:', settings)
    try {
      // Update settings sequentially to avoid race conditions
      if (settings.enabled !== undefined) {
        console.log('📝 Updating line_notifications_enabled:', settings.enabled)
        await this.setSetting('line_notifications_enabled', settings.enabled, 'Enable/disable LINE notifications globally', 'line')
      }

      if (settings.channelAccessToken !== undefined) {
        console.log('📝 Updating line_channel_access_token:', settings.channelAccessToken ? '***' : '(empty)')
        await this.setSetting('line_channel_access_token', settings.channelAccessToken, 'LINE Channel Access Token', 'line')

        // Update LineConfigLoader cache
        const { lineConfigLoader } = await import('./LineConfigLoader')
        await lineConfigLoader.updateConfig(settings.channelAccessToken, settings.enabled ?? true)
      }

      if (settings.webhookUrl !== undefined) {
        console.log('📝 Updating line_webhook_url:', settings.webhookUrl)
        await this.setSetting('line_webhook_url', settings.webhookUrl, 'LINE Webhook URL for receiving messages', 'line')
      }

      if (settings.templates !== undefined) {
        console.log('📝 Updating line_notification_templates')
        await this.setSetting('line_notification_templates', settings.templates, 'Custom notification templates', 'line')
      }

      if (settings.retryAttempts !== undefined) {
        console.log('📝 Updating notification_retry_attempts:', settings.retryAttempts)
        await this.setSetting('notification_retry_attempts', settings.retryAttempts, 'Number of retry attempts for failed notifications', 'notifications')
      }

      if (settings.retryDelay !== undefined) {
        console.log('📝 Updating notification_retry_delay:', settings.retryDelay)
        await this.setSetting('notification_retry_delay', settings.retryDelay, 'Delay between retry attempts in milliseconds', 'notifications')
      }

      console.log('✅ SettingsService: All settings updated successfully')
    } catch (error) {
      console.error('❌ SettingsService: Failed to update settings:', error)
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'SettingsService',
        'updateLineNotificationSettings',
        settings
      )
      throw error
    }
  }

  /**
   * Test LINE notification settings with Flex Message
   */
  async testLineNotificationSettings(userId: string, message: string = 'ทดสอบการตั้งค่า LINE notification'): Promise<boolean> {
    try {
      // Use LineConfigLoader to get token (will use cache if available)
      const { lineConfigLoader } = await import('./LineConfigLoader')

      // Check if config is already loaded in cache (don't force refresh to avoid circular dependency)
      const token = lineConfigLoader.getTokenSync()

      if (!token) {
        throw new Error('LINE Channel Access Token not configured. Please set it in Notifications > Settings.')
      }

      console.log('🧪 Testing with token from cache:', token.substring(0, 20) + '...')

      // Create Flex Message for testing
      const testFlexMessage = {
        type: 'flex',
        altText: 'ทดสอบการตั้งค่า LINE notification',
        contents: {
          type: 'bubble',
          size: 'mega',
          header: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'box',
                layout: 'horizontal',
                contents: [
                  {
                    type: 'text',
                    text: '🧪',
                    size: 'xxl',
                    flex: 0,
                    margin: 'none'
                  }
                ]
              },
              {
                type: 'text',
                text: 'ทดสอบการตั้งค่า',
                weight: 'bold',
                size: 'xl',
                color: '#17C964',
                margin: 'md'
              }
            ],
            paddingAll: '20px',
            paddingBottom: '16px',
            spacing: 'none',
            backgroundColor: '#ffffff'
          },
          body: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'box',
                layout: 'horizontal',
                contents: [
                  {
                    type: 'text',
                    text: '✅',
                    size: 'sm',
                    color: '#999999',
                    flex: 0,
                    margin: 'none'
                  },
                  {
                    type: 'text',
                    text: 'สถานะ',
                    size: 'sm',
                    color: '#999999',
                    flex: 0,
                    margin: 'md'
                  },
                  {
                    type: 'text',
                    text: 'ทำงานปกติ',
                    size: 'sm',
                    color: '#17C964',
                    align: 'end',
                    weight: 'bold'
                  }
                ]
              },
              {
                type: 'box',
                layout: 'horizontal',
                contents: [
                  {
                    type: 'text',
                    text: '⏰',
                    size: 'sm',
                    color: '#999999',
                    flex: 0
                  },
                  {
                    type: 'text',
                    text: 'เวลา',
                    size: 'sm',
                    color: '#999999',
                    flex: 0,
                    margin: 'md'
                  },
                  {
                    type: 'text',
                    text: new Date().toLocaleString('th-TH', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    }),
                    size: 'sm',
                    color: '#111111',
                    align: 'end'
                  }
                ],
                margin: 'md'
              },
              {
                type: 'separator',
                margin: 'xl'
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'text',
                    text: '📝 ข้อความทดสอบ',
                    size: 'sm',
                    color: '#999999',
                    margin: 'none'
                  },
                  {
                    type: 'text',
                    text: message,
                    size: 'sm',
                    color: '#111111',
                    wrap: true,
                    margin: 'xs'
                  }
                ],
                margin: 'xl'
              }
            ],
            paddingAll: '20px',
            backgroundColor: '#ffffff'
          },
          footer: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'button',
                action: {
                  type: 'uri',
                  label: 'เปิดระบบ',
                  uri: window.location.origin
                },
                style: 'primary',
                color: '#17C964',
                height: 'sm'
              }
            ],
            paddingAll: '12px',
            backgroundColor: '#ffffff'
          },
          styles: {
            header: { separator: false },
            body: { separator: false },
            footer: { separator: false }
          }
        }
      }

      // Use direct LINE API call via our handler
      const { sendLineMessage } = await import('../api/line-send-handler')
      const result = await sendLineMessage(userId, testFlexMessage, 'flex')

      if (result.success) {
        return true
      } else {
        throw new Error(result.error || 'Failed to send test message')
      }
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'SettingsService',
        'testLineNotificationSettings',
        { userId, message }
      )
      return false
    }
  }



  /**
   * Get UI settings
   */
  async getUISettings(): Promise<UISettings> {
    try {
      const [
        theme,
        language,
        showConnectionStatus
      ] = await Promise.all([
        this.getSettingValue('ui_theme', 'light'),
        this.getSettingValue('ui_language', 'th'),
        this.getSettingValue('ui_show_connection_status', true)
      ])

      return {
        theme: theme === 'dark' ? 'dark' : 'light',
        language: language === 'en' ? 'en' : 'th',
        showConnectionStatus: this.toBooleanValue(showConnectionStatus)
      }
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'SettingsService',
        'getUISettings'
      )
      
      // Return default settings on error
      return {
        theme: 'light',
        language: 'th',
        showConnectionStatus: true
      }
    }
  }

  /**
   * Update UI settings
   */
  async updateUISettings(settings: Partial<UISettings>): Promise<void> {
    try {
      // Update settings sequentially to avoid race conditions
      if (settings.theme !== undefined) {
        await this.setSetting('ui_theme', settings.theme, 'Application theme (light/dark)', 'ui')
      }

      if (settings.language !== undefined) {
        await this.setSetting('ui_language', settings.language, 'Application language', 'ui')
      }

      if (settings.showConnectionStatus !== undefined) {
        await this.setSetting('ui_show_connection_status', settings.showConnectionStatus, 'Show connection status indicator', 'ui')
      }
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'SettingsService',
        'updateUISettings',
        settings
      )
      throw error
    }
  }

  /**
   * Initialize default settings if they don't exist
   */
  async initializeDefaultSettings(): Promise<void> {
    try {
      // Check if any settings exist first
      const { data: existingSettings, error: checkError } = await supabase
        .from('settings')
        .select('key')
        .limit(1)

      if (checkError) {
        console.warn('Failed to check existing settings:', checkError)
        return
      }

      // If settings already exist, skip initialization
      if (existingSettings && existingSettings.length > 0) {
        console.log('Settings already initialized, skipping default initialization')
        return
      }

      const defaultSettings = [
        // LINE notification settings
        { key: 'line_notifications_enabled', value: false, description: 'Enable/disable LINE notifications globally', category: 'line' },
        { key: 'line_channel_access_token', value: '', description: 'LINE Channel Access Token', category: 'line' },
        { key: 'line_webhook_url', value: '', description: 'LINE Webhook URL for receiving messages', category: 'line' },
        { key: 'line_notification_templates', value: {}, description: 'Custom notification templates', category: 'line' },
        
        // Notification settings
        { key: 'notification_retry_attempts', value: 3, description: 'Number of retry attempts for failed notifications', category: 'notifications' },
        { key: 'notification_retry_delay', value: 1000, description: 'Delay between retry attempts in milliseconds', category: 'notifications' },
        

        
        // UI settings
        { key: 'ui_theme', value: 'light', description: 'Application theme (light/dark)', category: 'ui' },
        { key: 'ui_language', value: 'th', description: 'Application language', category: 'ui' },
        { key: 'ui_show_connection_status', value: true, description: 'Show connection status indicator', category: 'ui' }
      ]

      // Insert all default settings at once to avoid race conditions
      const { error: insertError } = await supabase
        .from('settings')
        .insert(defaultSettings)

      if (insertError && insertError.code !== '23505') {
        console.warn('Failed to initialize default settings:', insertError)
      }
    } catch (error) {
      console.warn('Failed to initialize default settings:', error)
    }
  }
}

// Export singleton instance
export const settingsService = new SettingsService()